/**
 * Hook para gerenciar seleção e busca de clientes
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Cliente } from '../../../../types/gerArquivos';
import { GerArquivosService } from '../../../../services/gerArquivosService';
import { useClientes } from '../../../../hooks/useClientes';
import { ClienteSelectionState } from '../types';
import { isValidWhatsApp, normalizeWhatsApp } from '../../../../utils/whatsapp';

export function useClienteSelection() {
  const [state, setState] = useState<ClienteSelectionState>({
    cliente: null,
    query: '',
    digits: '',
    matchStatus: null,
    isConfirmed: false,
    resumo: ''
  });

  const { clientes, carregando: carregandoClientes, buscarClientes, limparBusca } = useClientes();

  /**
   * Formata o resumo do cliente para exibição
   */
  const formatClienteResumo = useCallback((cliente: Cliente, digits: string): string => {
    const partes: string[] = [`#${cliente.id}`];
    const principal = cliente.empresa || cliente.nome;
    if (principal) partes.push(principal);
    if (cliente.cnpjCpf) partes.push(cliente.cnpjCpf);
    if (cliente.cidade) partes.push(cliente.cidade);
    if (digits) partes.push(`WhatsApp: ${digits}`);
    return partes.join(' · ');
  }, []);

  /**
   * Normaliza dígitos do WhatsApp (remove DDI 55, completa DDD 62)
   */
  const normalizeDigits = useCallback((raw: string): string => {
    return normalizeWhatsApp(raw);
  }, []);

  /**
   * Busca automática por WhatsApp (debounced)
   */
  useEffect(() => {
    const q = state.query.trim();
    const normalized = normalizeDigits(q);
    // Considera como WhatsApp se tiver 10 ou 11 dígitos E for válido
    const isWhatsAppValid = (normalized.length === 10 || normalized.length === 11) && isValidWhatsApp(normalized);
    // Verifica se parece ser WhatsApp (tem 6+ dígitos mas ainda não é válido)
    const looksLikeWhatsApp = normalized.length >= 6 && normalized.length <= 11;

    // Atualizar dígitos no estado
    if (normalized !== state.digits) {
      setState(prev => ({ ...prev, digits: normalized }));
    }

    const timer = setTimeout(async () => {
      // Se já está confirmado, não fazer nada (usuário já escolheu)
      if (state.isConfirmed) {
        return;
      }

      if (isWhatsAppValid) {
        // Buscar por WhatsApp (só busca se for válido)
        try {
          const found = await GerArquivosService.buscarClientePorWhatsapp(normalized);
          if (found) {
            const resumo = formatClienteResumo(found, normalized);
            setState(prev => ({
              ...prev,
              cliente: found,
              matchStatus: 'found',
              resumo
            }));
            limparBusca();
          } else {
            setState(prev => ({
              ...prev,
              cliente: null,
              matchStatus: 'not_found',
              resumo: ''
            }));
          }
        } catch {
          setState(prev => ({ ...prev, matchStatus: null }));
        }
      } else if (q.length >= 2 && !looksLikeWhatsApp) {
        // Buscar por nome (apenas se não parecer ser WhatsApp)
        buscarClientes(q);
        setState(prev => ({ ...prev, matchStatus: null, cliente: null, resumo: '' }));
      } else {
        limparBusca();
        setState(prev => ({ ...prev, matchStatus: null, cliente: null, resumo: '' }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [state.query, state.isConfirmed, state.digits, normalizeDigits, formatClienteResumo, buscarClientes, limparBusca]);

  /**
   * Atualiza a query de busca
   */
  const setQuery = useCallback((query: string) => {
    setState(prev => ({
      ...prev,
      query,
      cliente: null,
      isConfirmed: false,
      matchStatus: null,
      resumo: ''
    }));
  }, []);

  /**
   * Seleciona um cliente da lista (auto-confirma)
   */
  const selectCliente = useCallback((cliente: Cliente) => {
    const digits = normalizeDigits(cliente.whatsapp || '');
    const resumo = formatClienteResumo(cliente, digits);
    
    // Ao selecionar da lista, já confirma automaticamente
    setState(prev => ({
      ...prev,
      cliente,
      digits,
      query: resumo, // Preenche com o resumo completo
      matchStatus: 'found',
      isConfirmed: true, // Auto-confirma
      resumo
    }));
    
    limparBusca(); // Limpa a lista de sugestões
  }, [normalizeDigits, formatClienteResumo, limparBusca]);

  /**
   * Confirma a seleção do cliente (preenche input com resumo)
   */
  const confirmCliente = useCallback(() => {
    if (!state.cliente) return;
    setState(prev => ({
      ...prev,
      query: prev.resumo,
      isConfirmed: true
    }));
  }, [state.cliente]);

  /**
   * Remove/limpa o cliente selecionado
   */
  const clearCliente = useCallback(() => {
    setState({
      cliente: null,
      query: '',
      digits: '',
      matchStatus: null,
      isConfirmed: false,
      resumo: ''
    });
  }, []);

  /**
   * Reset completo do estado
   */
  const reset = useCallback(() => {
    clearCliente();
    limparBusca();
  }, [clearCliente, limparBusca]);

  const isWhatsValid = useMemo(() => {
    // Se não tem dígitos, não é válido
    if (!state.digits || state.digits.length === 0) {
      return false;
    }
    // Validação rigorosa: deve ter 10 ou 11 dígitos e ser um WhatsApp válido
    return isValidWhatsApp(state.digits);
  }, [state.digits]);
  
  // Verifica se está digitando WhatsApp (tem 6+ dígitos mas ainda não é válido)
  const isTypingWhatsApp = useMemo(() => {
    if (state.isConfirmed || state.cliente || state.matchStatus) return false;
    const digits = state.digits || '';
    // Só mostra mensagem se parece ser WhatsApp (6+ dígitos) mas não é válido
    return digits.length >= 6 && digits.length <= 11 && !isWhatsValid;
  }, [state.digits, state.isConfirmed, state.cliente, state.matchStatus, isWhatsValid]);
  
  const hasCadastro = useMemo(() => !!state.cliente && state.isConfirmed, [state.cliente, state.isConfirmed]);

  return {
    // Estado
    ...state,
    clientes,
    carregandoClientes,
    isWhatsValid,
    isTypingWhatsApp,
    hasCadastro,
    
    // Ações
    setQuery,
    selectCliente,
    confirmCliente,
    clearCliente,
    reset
  };
}

